/**
 * Canonical ad-image normalisation (2026-06-01f).
 *
 * Single source of truth for both `MyAds` cards and `AdDetails` so the photo
 * shown on the listing card is always identical to the photo rendered on the
 * details page.
 *
 * Bug class fixed:
 *   - MyAds was reading `ad.images[0]` as a bare string (no URL prefix), then
 *     placing it directly in <img src>. In production this 404'd because
 *     `/uploads/abc.jpg` resolves to the SPA host, not the backend.
 *   - AdDetails was running `guardImage` which replaces ANY URL matching a
 *     seed-image pattern (Unsplash, Picsum, etc.) with a category placeholder
 *     — but MyAds did not, so the same ad showed one image on the card and a
 *     placeholder on details.
 *   - Different field names (`images`, `imageUrls`, `photos`, `thumbnail`)
 *     existed across legacy / book / opportunity records, and no caller
 *     handled all of them.
 *
 * Contract:
 *   normalizeAdImages(ad) → string[]   // absolute URLs, deduped, non-empty
 *   getAdCoverImage(ad)   → string     // first absolute URL, or '' if none
 *
 * Both functions never throw and never return `null` — callers can use
 * `.length === 0` / `''` as the "no image" signal and render a placeholder
 * only when the array is empty OR after the browser fires `onError` on the
 * real URL.
 */

import { toAbsoluteUrl } from './media';

// Fields we accept, in priority order. The first non-empty array wins.
// iter274 — added `image_urls` and `media` (sometimes used by older
// imports / opportunity rows) so a single ad object never silently
// shows a placeholder just because the field name differs.
const IMAGE_FIELDS = ['images', 'imageUrls', 'image_urls', 'photos', 'media'];
// Optional single-URL fallback field
const SINGLE_FIELDS = [
  'thumbnail',
  'image',
  'imageUrl',
  'image_url',
  'photo_url',
  'cover',
  'cover_url',
];

function extractRaw(ad) {
  if (!ad || typeof ad !== 'object') return [];

  for (const field of IMAGE_FIELDS) {
    const value = ad[field];
    if (Array.isArray(value) && value.length > 0) {
      // Drop null / blank / undefined entries before returning so the
      // first-non-empty-array rule does not return an array of empties.
      const cleaned = value.filter((v) => {
        if (v == null) return false;
        if (typeof v === 'string') return v.trim().length > 0;
        if (typeof v === 'object') return !!(v.url || v.path || v.src || v.secure_url);
        return false;
      });
      if (cleaned.length > 0) return cleaned;
    }
  }

  // Fall back to single-URL fields
  for (const field of SINGLE_FIELDS) {
    const value = ad[field];
    if (value && (typeof value !== 'string' || value.trim())) return [value];
  }

  return [];
}

// iter274 — reject ephemeral / non-persistent URLs from the resolver
// so a card never silently keeps a blob: / data: link in <img src>
// (those die on hard refresh + are not portable across devices).
function isPersistentUrl(url) {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  if (trimmed.startsWith('blob:')) return false;
  if (trimmed.startsWith('data:')) return false;
  return true;
}

function coerceToUrl(entry) {
  if (!entry) return '';
  if (typeof entry === 'string') return entry.trim();
  if (typeof entry === 'object') {
    // Backend variations: { url }, { path }, { src }, { secure_url }
    return (entry.url || entry.path || entry.src || entry.secure_url || '').trim();
  }
  return '';
}

/**
 * Build the canonical image list for an ad.
 *
 * @param {object} ad - Ad document from /api/ads/{id} or /api/ads/user/me
 * @returns {string[]} Absolute URLs, deduplicated, in original order.
 */
export function normalizeAdImages(ad) {
  const raw = extractRaw(ad);
  const seen = new Set();
  const out = [];

  for (const entry of raw) {
    const url = coerceToUrl(entry);
    if (!url) continue;
    if (!isPersistentUrl(url)) continue;
    // Resolve relative paths (/uploads/...) → absolute backend URL.
    // Pass http(s) URLs through untouched.
    const abs = toAbsoluteUrl(url);
    if (!abs) continue;
    if (seen.has(abs)) continue;
    seen.add(abs);
    out.push(abs);
  }

  return out;
}

/**
 * Get the first available image URL for a card thumbnail.
 *
 * @param {object} ad
 * @returns {string} Absolute URL or '' if the ad has no image.
 */
export function getAdCoverImage(ad) {
  const list = normalizeAdImages(ad);
  return list[0] || '';
}

export default { normalizeAdImages, getAdCoverImage };
